import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import RawCaptureTimelinePreview from '../../components/Debug/RawCaptureTimelinePreview';
import { getCaptureFirstAiErrorMessage } from './captureFirstAiErrors';
import {
  captureAndOrganizeCareNote,
  runCaptureFirstExtraction,
} from './captureFirstExtractionService';
import {
  DEMO_MASTER_MEDICATIONS,
  getMedicationContextLabel,
} from './sandboxMedicationContext';

// Experimental preview panel. Promote or delete with docs/experiments/raw-care-capture-extraction.md.
const REVIEW_STATE_LABELS = {
  ready_to_save: {
    label: 'Ready to save',
    color: 'success',
  },
  needs_review: {
    label: 'Needs review',
    color: 'warning',
  },
};

const cleanLabel = (value) => String(value || '').replace(/_/g, ' ').trim();

const ReviewStateChip = ({ state }) => {
  const config = REVIEW_STATE_LABELS[state] || REVIEW_STATE_LABELS.needs_review;
  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      sx={{ fontWeight: 700 }}
    />
  );
};

const CountTile = ({ label, value, tone = 'default' }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.5,
      borderRadius: 1,
      bgcolor: tone === 'ready' ? '#F3FBF7' : tone === 'review' ? '#FFF8E6' : '#fff',
      borderColor: tone === 'ready' ? '#B8DFC9' : tone === 'review' ? '#F1D49C' : 'divider',
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="h6" sx={{ fontWeight: 800 }}>
      {value}
    </Typography>
  </Paper>
);

const DemoMedicationContext = () => (
  <Alert severity="info">
    <Stack spacing={1}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        Sandbox medication context
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={0.75}>
        {DEMO_MASTER_MEDICATIONS.map((medication) => (
          <Chip
            key={medication.id}
            label={getMedicationContextLabel(medication)}
            size="small"
            variant="outlined"
          />
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Used only to test whether a routine medication mention can resolve against a master medication.
      </Typography>
    </Stack>
  </Alert>
);

const UnderstandingSummary = ({ understanding = {} }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
    <Stack spacing={1.25}>
      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
        Stage 2: AI understanding
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={0.75}>
        <Chip label={cleanLabel(understanding.dominantMeaning || 'unknown meaning')} size="small" />
        <Chip label={cleanLabel(understanding.splitStrategy || 'unknown split')} size="small" variant="outlined" />
        <Chip label={`Confidence: ${understanding.confidence || 'low'}`} size="small" variant="outlined" />
        {understanding.needsReview ? <Chip label="Understanding needs review" size="small" color="warning" /> : null}
      </Stack>
      {understanding.reason ? (
        <Typography variant="body2" color="text.secondary">
          {understanding.reason}
        </Typography>
      ) : null}
      {understanding.supportingAspects?.length ? (
        <Stack direction="row" flexWrap="wrap" gap={0.75}>
          {understanding.supportingAspects.map((aspect) => (
            <Chip key={aspect} label={cleanLabel(aspect)} size="small" variant="outlined" />
          ))}
        </Stack>
      ) : null}
    </Stack>
  </Paper>
);

const RecordRoutingSummary = ({ records = [] }) => {
  if (!records.length) {
    return null;
  }

  return (
    <Stack spacing={1}>
      {records.map((record, index) => (
        <Paper key={`${record.candidateType}-${index}`} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
          <Stack spacing={0.75}>
            <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="center">
              <ReviewStateChip state={record.reviewState} />
              <Chip label={record.userFacingType || cleanLabel(record.candidateType)} size="small" variant="outlined" />
              <Chip label={`Confidence: ${record.confidence}`} size="small" variant="outlined" />
            </Stack>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {record.sourceText}
            </Typography>
            {record.reviewReasons?.length ? (
              <Typography variant="caption" color="text.secondary">
                Review reason: {record.reviewReasons.join('; ')}
              </Typography>
            ) : null}
            {record.medicationResolution ? (
              <Typography variant="caption" color="text.secondary">
                Medication match: {record.medicationResolution.resolved ? 'resolved' : 'needs review'}
                {record.medicationResolution.medicationName ? ` · ${record.medicationResolution.medicationName}` : ''}
                {record.medicationResolution.scheduledTime ? ` @ ${record.medicationResolution.scheduledTime}` : ''}
              </Typography>
            ) : null}
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

const CaptureFirstExtractionPanel = ({ rawText = '', sourceType = 'unknown', child = null }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveResult, setSaveResult] = useState(null);
  const canRun = Boolean(String(rawText || '').trim());
  const canSave = canRun && Boolean(child?.id);
  const saveDisabledReason = !canRun
    ? 'Add a note before saving to the timeline.'
    : !child?.id
      ? 'Choose a profile above before saving to the timeline.'
      : '';

  useEffect(() => {
    setResult(null);
    setError('');
    setSaveResult(null);
  }, [rawText, sourceType, child?.id]);

  const groupedRecords = useMemo(() => {
    const records = Array.isArray(result?.suggestedRecords) ? result.suggestedRecords : [];
    return {
      ready: records.filter((record) => record.reviewState === 'ready_to_save'),
      review: records.filter((record) => record.reviewState !== 'ready_to_save'),
    };
  }, [result]);

  const handleRun = async () => {
    setLoading(true);
    setError('');

    try {
      const nextResult = await runCaptureFirstExtraction({
        rawText,
        sourceType,
        options: {
          medications: DEMO_MASTER_MEDICATIONS,
        },
      });

      if (nextResult?.ok === false) {
        setResult(nextResult);
        setError(getCaptureFirstAiErrorMessage(nextResult.error || 'Capture-first AI extraction did not return suggested records.'));
        return;
      }

      setResult(nextResult);
    } catch (runError) {
      setResult(null);
      setError(getCaptureFirstAiErrorMessage(runError));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToTimeline = async () => {
    setSaving(true);
    setError('');
    setSaveResult(null);

    try {
      const organized = await captureAndOrganizeCareNote({
        childId: child?.id,
        rawText,
        sourceType,
        options: {
          medications: DEMO_MASTER_MEDICATIONS,
        },
      });
      if (organized?.extractionResult) {
        setResult(organized.extractionResult);
      }
      setSaveResult({
        sourceNoteId: organized.sourceNoteId,
        status: organized.status || (organized.failedCount || organized.needsReviewCount ? 'needs_review' : 'organized'),
        needsReview: Boolean(organized.needsReviewCount),
        readyToSaveCount: organized.readyToSaveCount || 0,
        needsReviewCount: organized.needsReviewCount || 0,
        recordCount: organized.savedCount || 0,
        expectedRecordCount: organized.suggestedCount || 0,
        failedCount: organized.failedCount || 0,
      });
    } catch (saveError) {
      setError(getCaptureFirstAiErrorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Capture-first backend experiment
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tests raw capture, AI understanding, and structured suggestions with light review routing.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="outlined" onClick={handleRun} disabled={loading || !canRun || saving}>
              {loading ? 'Running AI...' : 'Run capture-first AI'}
            </Button>
            <Box>
              <Button variant="contained" onClick={handleSaveToTimeline} disabled={saving || !canSave}>
                {saving ? 'Saving...' : 'Save to timeline'}
              </Button>
              {!canSave && saveDisabledReason ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {saveDisabledReason}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </Stack>

        <DemoMedicationContext />

        {!child?.id ? (
          <Alert severity="info">
            Choose a profile context above to test saving this captured note to the timeline.
          </Alert>
        ) : null}

        {error ? <Alert severity="warning">{error}</Alert> : null}

        {saveResult ? (
          <Alert severity={saveResult.status === 'needs_review' ? 'warning' : 'success'}>
            <Stack spacing={0.75}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {saveResult.status === 'needs_review'
                  ? `Needs review. ${saveResult.recordCount || 0} detail${saveResult.recordCount === 1 ? '' : 's'} saved to timeline.`
                  : saveResult.status === 'organized'
                  ? `Saved to timeline. ${saveResult.recordCount || 0} detail${saveResult.recordCount === 1 ? '' : 's'} found.`
                  : 'Saved source note. Finding details...'}
              </Typography>
              {(saveResult.status === 'organized' || saveResult.status === 'needs_review') && saveResult.expectedRecordCount > saveResult.recordCount ? (
                <Typography variant="caption">
                  {saveResult.expectedRecordCount - saveResult.recordCount} suggested detail{saveResult.expectedRecordCount - saveResult.recordCount === 1 ? '' : 's'} could not be saved yet.
                </Typography>
              ) : null}
              {saveResult.needsReview ? (
                <Typography variant="caption">
                  {saveResult.needsReviewCount} suggested detail{saveResult.needsReviewCount === 1 ? '' : 's'} may need a look.
                </Typography>
              ) : null}
              <Link component={RouterLink} to="/dashboard" underline="hover" sx={{ fontWeight: 700 }}>
                View timeline
              </Link>
            </Stack>
          </Alert>
        ) : null}

        {result ? (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
              <Stack spacing={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Stage 1: raw capture
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {result.source?.rawText || rawText}
                </Typography>
                {result.cleanup?.changed ? (
                  <Typography variant="caption" color="text.secondary">
                    AI understanding used cleaned text, while the original raw note stayed preserved.
                  </Typography>
                ) : null}
              </Stack>
            </Paper>

            <UnderstandingSummary understanding={result.understanding} />

            <Box
              sx={{
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
              }}
            >
              <CountTile label="Suggested records" value={result.suggestedRecords?.length || 0} />
              <CountTile label="Ready to save" value={result.readyToSaveCount || 0} tone="ready" />
              <CountTile label="Needs review" value={result.needsReviewCount || 0} tone="review" />
            </Box>

            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                alignItems: 'start',
              }}
            >
              <RawCaptureTimelinePreview
                title="Ready to save cards"
                candidates={groupedRecords.ready}
                emptyLabel="No high-confidence ready-to-save suggestions."
              />
              <RawCaptureTimelinePreview
                title="Needs review cards"
                candidates={groupedRecords.review}
                emptyLabel="No suggestions need review."
              />
            </Box>

            <RecordRoutingSummary records={result.suggestedRecords} />

            <Alert severity="info">
              Experimental path only. This saves a preserved Daily Log note from this sandbox and does not change Quick Log or import behavior.
            </Alert>
          </Stack>
        ) : (
          <Alert severity="info">
            Run this after editing the note to see which suggestions are ready to save and which should go to review.
          </Alert>
        )}
      </Stack>
    </Paper>
  );
};

export default CaptureFirstExtractionPanel;
